import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './HomeMap2.css';
import MapCard from './MapCard';
import cardsData from '../data/homeMapCards.json';
import homeMapSvg from '../data/homeMapSvg';

const AUTO_ROTATE_FALLBACK = 5000;

function normaliseCards(cards, publicUrl) {
  if (!Array.isArray(cards)) {
    return [];
  }

  return cards
    .map((card, index) => {
      if (!card || typeof card !== 'object') {
        return null;
      }

      const name = card.name || `region-${index}`;

      return {
        name,
        area: card.area || '',
        description: card.description || '',
        image: card.image ? `${publicUrl}${card.image}` : '',
      };
    })
    .filter(Boolean);
}

function HomeMap2({ cards = cardsData, autoRotateInterval = AUTO_ROTATE_FALLBACK }) {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const mappedCards = useMemo(() => normaliseCards(cards, publicUrl), [cards, publicUrl]);
  const defaultIndex = mappedCards.findIndex((card) => card.name === 'vietnam');
  const initialIndex = defaultIndex >= 0 ? defaultIndex : 0;

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const intervalRef = useRef(null);
  const mapContainerRef = useRef(null);

  const safeInterval = Math.max(autoRotateInterval, AUTO_ROTATE_FALLBACK);

  const stopAuto = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAuto = useCallback(() => {
    stopAuto();
    if (mappedCards.length <= 1) {
      return;
    }
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % mappedCards.length);
    }, safeInterval);
  }, [mappedCards.length, safeInterval, stopAuto]);

  useEffect(() => {
    setActiveIndex((prev) => {
      if (mappedCards.length === 0) {
        return 0;
      }
      if (prev >= mappedCards.length) {
        return 0;
      }
      return prev;
    });
    startAuto();
    return stopAuto;
  }, [mappedCards, startAuto, stopAuto]);

  const handleSelect = useCallback(
    (regionName) => {
      const index = mappedCards.findIndex((card) => card.name === regionName);
      if (index === -1) {
        return;
      }
      setActiveIndex(index);
      startAuto();
    },
    [mappedCards, startAuto]
  );

  const handlePause = useCallback(() => {
    stopAuto();
  }, [stopAuto]);

  const handleResume = useCallback(() => {
    startAuto();
  }, [startAuto]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) {
      return undefined;
    }

    container.innerHTML = homeMapSvg;

    const disposers = [];

    mappedCards.forEach((card) => {
      const element = container.querySelector(`#${card.name}`);
      if (!element) {
        return;
      }

      const clickHandler = () => handleSelect(card.name);
      const keyHandler = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSelect(card.name);
        }
      };

      if (!element.dataset.originalFill) {
        element.dataset.originalFill = element.getAttribute('fill') || '';
      }

      if (!element.dataset.originalStroke) {
        element.dataset.originalStroke = element.getAttribute('stroke') || '';
      }

      element.setAttribute('role', 'button');
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-label', `${card.area || card.name} 地區`);

      element.addEventListener('click', clickHandler);
      element.addEventListener('keydown', keyHandler);

      disposers.push(() => {
        element.removeEventListener('click', clickHandler);
        element.removeEventListener('keydown', keyHandler);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [mappedCards, handleSelect]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) {
      return;
    }

    mappedCards.forEach((card, index) => {
      const element = container.querySelector(`#${card.name}`);
      if (!element) {
        return;
      }
      if (index === activeIndex) {
        element.classList.add('is-active');
        element.setAttribute('aria-pressed', 'true');
      } else {
        element.classList.remove('is-active');
        element.setAttribute('aria-pressed', 'false');
      }
    });
  }, [mappedCards, activeIndex]);

  const introImage = `${publicUrl}/home-map-intro-text.png`;
  const decorPrimary = `${publicUrl}/home-map-decor-primary.png`;

  if (!mappedCards.length) {
    return null;
  }

  return (
    <section className="home-map-2" onMouseLeave={handleResume}>
      <div className="home-map-2__intro">
        <img className="home-map-2__text-image" src={introImage} alt="至善足跡" />
        <p className="home-map-2__intro-text">
          至善立足台灣30年，秉持尊重多元文化與愛心無國界之理念，在亞洲從事兒少發展工作，服務範圍涵蓋
          <span className="map-card__area"> 台灣</span>、
          <span className="map-card__area"> 越南</span>、
          <span className="map-card__area"> 雲南</span>、
          <span className="map-card__area"> 緬甸</span>。
        </p>
        <div
          className="home-map-2__cards"
          onMouseEnter={handlePause}
          onFocus={handlePause}
          onBlur={handleResume}
        >
          {mappedCards.map((card, index) => (
            <div key={card.name} className={`home-map-2__card-layer ${index === activeIndex ? 'is-active' : ''}`}>
              <MapCard
                area={card.area}
                description={card.description}
                image={card.image}
                alt={`${card.area} 服務據點`}
              />
            </div>
          ))}
        </div>
      </div>

      <div
        className="home-map-2__visual-wrapper"
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
        onFocus={handlePause}
        onBlur={handleResume}
      >
        <div className="home-map-2__visual" ref={mapContainerRef} aria-hidden="false" />
      </div>

      <img className="home-map-2__decor-primary" src={decorPrimary} alt="" aria-hidden="true" />
    </section>
  );
}

export default HomeMap2;
